import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import { debounce } from '../../../services/utilityFunctions';

/**
 *  Google location search functionality as a searchable dropdown,
 *  should return the address details on dropdown selection
 */
export default function GoogleInput(props) {
  const {
    id,
    name,
    onChange,
    defaultValue,
    placeholder,
    onRequestStart,
    onRequestEnd,
    updateValue,
    onSetValue,
    disabled,
    filterByCountry,
    endpoint,
    onPlaceLocationSelection,
    maxLength,
  } = props;

  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [places, setPlaces] = useState([]);

  const fetchSuggestions = useCallback(
    debounce(({ value }) => {
      const params = {};
      if (value) {
        params.query = value;
      }
      if (filterByCountry) {
        params.country = filterByCountry;
      }
      axios
        .get(endpoint, {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
          params,
        })
        .then(({ data: response }) => {
          setPlaces(response.data);
        })
        .catch(err => {
          console.log(err);
        });
    }, 300),
    [filterByCountry],
  );

  useEffect(() => {
    if (updateValue) {
      setInputValue(updateValue);
    }
  }, [updateValue]);

  const handleSelection = (event, { suggestion }) => {
    event.preventDefault();
    setInputValue(suggestion.address);

    if (onRequestStart) {
      onRequestStart();
    }
    if (onPlaceLocationSelection && suggestion) {
      onPlaceLocationSelection(suggestion);
    }
    new Request()
      .get(`/address/${suggestion.id}`, undefined, true)
      .done(result => {
        onChange(result.data);
        if (onRequestEnd) {
          onRequestEnd();
        }
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_getAddress');
        if (onRequestEnd) {
          onRequestEnd();
        }
      });
  };

  const clearSuggestions = () => {
    setPlaces([]);
    // onChange(null);
  };
  const getSuggestionValue = address => address.address;
  const renderSuggestion = address => <div>{address.address}</div>;
  const shouldRenderSuggestions = value => value.trim().length > 2;

  return (
    <Autosuggest
      suggestions={places}
      onSuggestionsFetchRequested={fetchSuggestions}
      onSuggestionsClearRequested={clearSuggestions}
      getSuggestionValue={getSuggestionValue}
      onSuggestionSelected={handleSelection}
      renderSuggestion={renderSuggestion}
      shouldRenderSuggestions={shouldRenderSuggestions}
      inputProps={{
        id,
        name,
        placeholder,
        value: inputValue,
        onChange: ({ currentTarget }) => {
          if (onSetValue) {
            onSetValue(currentTarget.value);
          }
          setInputValue(currentTarget.value);
        },
        autocomplete: 'new-address1',
        disabled,
        maxLength,
      }}
    />
  );
}

GoogleInput.propTypes = {
  /** Input html id */
  id: PropTypes.string,
  /** Input html name */
  name: PropTypes.string,
  /** onChange handler, the details of the selected address are passed as an argument */
  onChange: PropTypes.func.isRequired,
  /** Input placeholder */
  placeholder: PropTypes.string.isRequired,
  onRequestStart: PropTypes.func,
  onRequestEnd: PropTypes.func,
  updateValue: PropTypes.string,
  onSetValue: PropTypes.func,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  filterByCountry: PropTypes.string,
  endpoint: PropTypes.string,
  onPlaceLocationSelection: PropTypes.func,
  maxLength: PropTypes.number,
};

GoogleInput.defaultProps = {
  id: 'googleInput',
  name: 'googleInput',
  defaultValue: '',
  onRequestStart: null,
  onRequestEnd: null,
  onSetValue: null,
  updateValue: null,
  disabled: false,
  filterByCountry: null,
  endpoint: '/api/address/autocomplete',
  onPlaceLocationSelection: null,
  maxLength: 35,
};
